import {
  Avatar,
  Box,
  Card,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useLocation, useMatch, useNavigate } from 'react-router';
import { PatientWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { useBinary, usePatient } from 'src/careflow/ph-core/fhir-client/fhir-react';
import Iconify from 'src/components/Iconify';
import useRouteHanderByTenant from 'src/careflow/ph-core/multi-tenant/tenant/hook/useRouteHanderByTenant';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import usePatientTabsStore from 'src/stores/patient-tabs';
import { getFhirIdFromEntity } from 'src/utils/fhir';
import { imageFromAttachment } from 'src/utils/formatPhoto';
import { validate as isUUID } from 'uuid';

interface PatientCardSearchProps {
  id: string;
  isLoading: boolean;
  patientsOrgsValid: any;
}

const defaultTab = 'general';

const PatientCardSearch = ({ id, isLoading, patientsOrgsValid }: PatientCardSearchProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { configurations } = useTenantConfigData();
  const location = useLocation();
  const [fileimg, setfileImg] = useState({});
  const closePatient = usePatientTabsStore((s) => s.closePatient);
  const [patient, { isFetching, isError }] = usePatient(id, {
    map: PatientWrapper,
  });
  const [binary] = useBinary(getFhirIdFromEntity(patient?.photo?.[0].url || ''));

  const pathUser = location.pathname?.slice(location.pathname?.lastIndexOf('/') + 1, location.pathname?.length);
  const path = isUUID(pathUser) ? ':patientId' : ':patientId/:currentTab';
  const urlMatch = useMatch(`/dashboard/crs/patient/${path}`);

  const isActive = usePatientTabsStore((s) => s.currentPatientId === id && urlMatch);
  const routeHandlerByTenant = useRouteHanderByTenant();
  const patientOrgValid = patientsOrgsValid?.find((e) => e?.patientId === id);
  const verifyOrg = patientOrgValid?.isCheckOrgValidate === true;

  useEffect(() => {
    if (binary) {
      setfileImg(binary);
    }
  }, [patient, binary]);

  function onClick() {
    const patientRoute = routeHandlerByTenant.redirectRouteAfterCreatePatient(
      `${id}/${urlMatch?.params.currentTab || defaultTab}` || '', 
      routeHandlerByTenant.Event.CARD_CLICKED, 
      location.pathname
    );
    navigate(patientRoute);
  }

  function onClose() {
    const nextPatient = closePatient(id);
    const patientOrgValid = patientsOrgsValid?.find((e) => e?.patientId === nextPatient);
    const verifyOrg = patientOrgValid?.isCheckOrgValidate === true;
    if (!nextPatient || !verifyOrg) {
      navigate(`/dashboard/app`);
    } else {
      const patientRoute = routeHandlerByTenant.redirectRouteAfterCreatePatient(
        `${nextPatient}/${urlMatch?.params.currentTab || defaultTab}` || '',
        routeHandlerByTenant.Event.CARD_CLICKED,location.pathname
      );
      navigate(patientRoute);
    }
  }

  useEffect(() => {
    if (isError) closePatient(id);
  }, [id, isError]);

  if(isLoading) return null;

  return (
    <Card
      variant="outlined"
      sx={{
        background: '#f4f6f8',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        borderColor: isActive ? theme.palette.primary.main : undefined,
        display: !verifyOrg ? 'none' : 'auto',
      }}
    >
      {isFetching ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: '16px 8px',
            width: '60px',
          }}
        >
          <CircularProgress size={20} />
        </Box>
      ) : (
        <>
          <Stack
            alignItems="center"
            direction="row"
            onClick={onClick}
            spacing={2}
            sx={{ p: '16px 8px' }}
          >
            <Avatar src={imageFromAttachment(fileimg)} />
            <Stack>
              <Tooltip title={patient?.getFullName()}>
                <Typography
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 150,
                  }}
                  variant="subtitle2"
                >
                  {patient?.getFullName()}
                </Typography>
              </Tooltip>
              <Typography variant="caption">{patient?.getMRN(configurations?.mpi?.systemMrn)?.value}</Typography>
            </Stack>
          </Stack>
          <IconButton
            sx={{
              position: 'absolute',
              right: 1,
              top: 1,
              padding: 0,
            }}
            onClick={onClose}
          >
            <Iconify
              icon="eva:close-circle-outline"
              sx={{
                color: 'red',
                height: 20,
                width: 20,
              }}
            />
          </IconButton>
        </>
      )}
    </Card>
  );
};

export default PatientCardSearch;
